import { AccountDataDecision } from './AccountDataDecision'
import { registerSignInDecision } from './signInDecision'
import { findRetainedAccountCopies, hasAccountEntryDecision, rememberAccountEntryDecision, WORKSPACE_ACCOUNT_IDENTITY_KEY, type RetainedAccountCopy } from '../data/workspaceInventory'
import type { AuthSession, AuthUser } from './authProvider'
import { useEffect, useState, type ReactNode } from 'react'
import { authProvider } from './sharedAuthProvider'
import { readCachedIdentity, clearLocalAuthentication, setInitialIdentity } from './cachedIdentity'
import { getSupabaseConfig } from '../supabase/config'
import { getDatabase, loadOrCreateDeviceId, readOrCreateBootstrapDeviceId } from '../data/db'
import { activateWorkspace, reconcileWorkspaceIdentity, activeWorkspace, assertWorkspaceActive, assertWorkspaceIdentity, chooseWorkspace, finishWorkspaceRetirement, holdWorkspaceLease, markWorkspaceSignedOut, onWorkspaceRetired, workspaceControlKey, workspaceSignedOut } from '../data/workspace'
import { clearAccountSurfaces } from './workspaceSurfaces'
import { guestDecisionNeeded, decideGuestWorkspace } from '../data/guestWorkspace'
import { finishPendingWorkspaceRemoval } from '../data/workspaceRemoval'
import { finishPendingGuestDataRemoval } from '../data/guestDataRemoval'
import { accountDeletedPending, clearAccountDeleted } from './accountDeletedFlag'

const config = getSupabaseConfig()
export const workspaceScope = { project: config?.url ?? 'local', environment: config?.environment ?? 'local' }
let initialization: Promise<AuthSession | null> | null = null
function initializeWorkspace(): Promise<AuthSession | null> {
  return initialization ??= (async () => {
    const initialize = async () => {
      await finishPendingGuestDataRemoval(workspaceScope)
      await finishPendingWorkspaceRemoval(workspaceScope)
      if (config && workspaceSignedOut(workspaceScope)) await clearLocalAuthentication(config)
      const initialControl = localStorage.getItem(workspaceControlKey(workspaceScope))
      const callbackPending = new URLSearchParams(location.search).has('code')
      const cached = callbackPending ? await authProvider.getSession() : config && !workspaceSignedOut(workspaceScope) ? await readCachedIdentity(config) : null
      const expectedControl = callbackPending ? localStorage.getItem(workspaceControlKey(workspaceScope)) : initialControl
      const selected = await chooseWorkspace(workspaceScope, cached?.user.id ?? null)
      reconcileWorkspaceIdentity(workspaceScope, cached?.user.id ?? null, expectedControl)
      setInitialIdentity(cached)
      activateWorkspace(selected)
      await holdWorkspaceLease()
      const database = await getDatabase()
      if (cached) {
        assertWorkspaceIdentity(cached.user.id)
        await database.put('meta', { key: WORKSPACE_ACCOUNT_IDENTITY_KEY, value: JSON.stringify({
          owner: cached.user.id, name: cached.user.name, email: cached.user.email,
        }) })
      }
      onWorkspaceRetired(clearAccountSurfaces)
      const bootstrapDeviceId = readOrCreateBootstrapDeviceId()
      const authoritativeDeviceId = await loadOrCreateDeviceId()
      if (authoritativeDeviceId !== bootstrapDeviceId) {
        window.location.reload()
        await new Promise<void>(() => undefined)
      }
      return cached
    }
    if (navigator.locks) return navigator.locks.request(workspaceControlKey(workspaceScope), initialize)
    return initialize()
  })()
}

/** Mounts immediately without private content. One document owns one immutable workspace. */
export function WorkspaceBoundary({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [locked, setLocked] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [guestChoice, setGuestChoice] = useState(false)
  const [legacyNotice, setLegacyNotice] = useState(false)
  const [entryChoice, setEntryChoice] = useState<{ copies: RetainedAccountCopy[]; account: AuthUser } | null>(null)
  useEffect(() => registerSignInDecision(async () => {
    // Google must identify the destination before a meaningful account comparison.
    // The post-OAuth boundary still blocks private content until the user decides.
    assertWorkspaceActive()
    return true
  }), [])
  useEffect(() => {
    let canceled = false
    const stop = onWorkspaceRetired(() => { if (!canceled) { setBusy(true); setLocked(true); setReady(false) } })
    const verify = () => {
      if (!activeWorkspace()) return
      try { assertWorkspaceActive(); delete document.documentElement.dataset.workspacePrivacy } catch { /* retirement owns the neutral UI */ }
    }
    const unsubscribe = authProvider.onSessionChange(session => {
      if (!activeWorkspace()) return
      try { assertWorkspaceIdentity(config ? session?.user.id ?? null : null) } catch { /* never expose the new identity in the old App */ }
    })
    const conceal = () => { document.documentElement.dataset.workspacePrivacy = 'suspended' }
    window.addEventListener('pagehide', conceal)
    window.addEventListener('storage', verify)
    window.addEventListener('pageshow', verify)
    document.addEventListener('visibilitychange', verify)
    void initializeWorkspace().then(async session => {
      if (canceled) return
      assertWorkspaceActive()
      const owner = activeWorkspace()?.owner ?? null
      const copies = await findRetainedAccountCopies(workspaceScope, owner)
      if (canceled) return
      assertWorkspaceActive()
      if (owner && session?.user.id === owner && !hasAccountEntryDecision(workspaceScope,owner,copies)) setEntryChoice({copies, account: session.user})
      const chooseGuest = await guestDecisionNeeded(workspaceScope)
      if (canceled) return
      setGuestChoice(chooseGuest)
      setLegacyNotice(Boolean(activeWorkspace()?.quarantinedLegacy))
      setReady(!chooseGuest)
    }).catch(() => { if (!canceled) setError('Your offline content could not be opened safely. Nothing has been removed. Please retry.') })
    return () => {
      canceled = true; stop(); unsubscribe()
      window.removeEventListener('pagehide', conceal)
      window.removeEventListener('storage', verify)
      window.removeEventListener('pageshow', verify)
      document.removeEventListener('visibilitychange', verify)
    }
  }, [])

  useEffect(() => {
    if (!locked) return
    // Let every synchronous retirement subscriber capture the outgoing draft first.
    setBusy(true)
    void Promise.resolve().then(async () => {
      await finishWorkspaceRetirement()
      if (config && workspaceSignedOut(workspaceScope)) await clearLocalAuthentication(config)
      await clearAccountSurfaces()
      await finishPendingWorkspaceRemoval(workspaceScope)
    }).then(() => {
      // After a deleted account there is nothing to decide: carry on into the guest workspace by itself.
      if (accountDeletedPending()) { clearAccountDeleted(); window.location.reload(); return }
      setBusy(false)
    }).catch(() => {
      setBusy(false)
      setError(accountDeletedPending()
        ? 'Your account has been deleted, but this device could not finish removing its copy. Close other miniMany tabs and press Retry.'
        : 'The account is hidden, but sign-out could not finish. No other account can open this copy. Close other miniMany tabs and retry.')
    })
  }, [locked])

  const reopen = async () => {
    setBusy(true)
    try {
      await finishWorkspaceRetirement()
      if (config && workspaceSignedOut(workspaceScope)) await clearLocalAuthentication(config)
      await clearAccountSurfaces()
      await finishPendingWorkspaceRemoval(workspaceScope)
      clearAccountDeleted()
      // Start a fresh JS lifecycle: indexes, undo stacks, object URLs and captured
      // callbacks from the previous account cannot survive this transition.
      window.location.reload()
    } catch { setError('Unable to finish saving this workspace. No offline copy has been removed.'); setBusy(false) }
  }
  const chooseGuest = async (add: boolean) => {
    setBusy(true); setError(null)
    try { await decideGuestWorkspace(add); setGuestChoice(false); setReady(true) }
    catch (caught) { setError(caught instanceof Error ? caught.message : 'Guest notes could not be added. Both copies are preserved.') }
    finally { setBusy(false) }
  }
  if (entryChoice && !locked) return <AccountDataDecision error={error} copies={entryChoice.copies} account={entryChoice.account} onKeep={() => {
    try {
      assertWorkspaceIdentity(entryChoice.account.id)
      rememberAccountEntryDecision(workspaceScope, entryChoice.account.id, entryChoice.copies)
      setEntryChoice(null)
    } catch { setError('Your choice could not be saved. Free browser storage and retry.'); }
  }} onCancel={() => markWorkspaceSignedOut()} />
  if (guestChoice && !locked) return <main className="workspace-boundary"><section className="workspace-boundary__panel">
    <h1>Your guest notes are separate.</h1>
    <p>You have notes saved without an account in this browser. Add a copy to the account you just signed in to, or keep them in the guest workspace. Nothing is uploaded until you choose.</p>
    {error && <p role="alert">{error}</p>}
    <div className="workspace-boundary__actions">
      <button className="btn" disabled={busy} onClick={() => markWorkspaceSignedOut()}>Cancel sign-in</button>
      <button className="btn" disabled={busy} onClick={() => void chooseGuest(false)}>Keep separate</button>
      <button className="btn" disabled={busy} onClick={() => void chooseGuest(true)}>Add guest notes to this account</button>
    </div>
  </section></main>
  if (ready && legacyNotice && !locked) return <main className="workspace-boundary"><section className="workspace-boundary__panel">
    <h1>An older offline copy is kept separately.</h1>
    <p>We couldn’t establish which account created it. To protect its contents, it has not been opened, uploaded or deleted. Your current workspace is separate.</p>
    <p>Keep this browser’s storage intact while the older copy’s ownership is reviewed.</p>
    <button className="btn" onClick={() => setLegacyNotice(false)}>Continue to this workspace</button>
  </section></main>
  if (ready && !locked) return <div className="account-workspace">{children}</div>
  const deleted = locked && accountDeletedPending()
  return <main className="workspace-boundary" aria-labelledby="workspace-heading">
    <section className="workspace-boundary__panel">
      <p className="workspace-boundary__brand">miniMany</p>
      <h1 id="workspace-heading">{deleted ? 'Your account has been deleted.' : locked ? 'Your account workspace is closed.' : 'Opening your workspace…'}</h1>
      <p role="status">{deleted
        ? 'Everything in your account is erased. Finishing up on this device…'
        : locked ? 'Retained offline copies stay separate. Continue to open the current account or your guest workspace.' : 'Checking which offline content belongs to this session.'}</p>
      {error && <p role="alert">{error}</p>}
      <div className="workspace-boundary__actions">
        {(locked || error) && !(deleted && !error) && <button className="btn" disabled={busy} onClick={() => void reopen()}>{error ? 'Retry' : 'Continue'}</button>}
      </div>
    </section>
  </main>
}
